import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Loader,
  Notification,
  Message
} from '../../web.common';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import {
  DisplayDate,
  DisplayGuid
} from '../items';
import { DetailsRightBar, NoLink, Divider } from './DetailsRightBar';

const ExternalPurchaseInternal = ({ api, match: { params: { externalPurchaseId } } }) => {
  const [externalPurchaseData, setExternalPurchaseData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.externalPurchase.getExternalPurchaseById(externalPurchaseId);
        if (response.success) {
          setExternalPurchaseData(response.data);
        } else {
          setErrorMessage(new Message('error', 'Unable to get data'));
        }
      } catch (e) {
        setErrorMessage(new Message('error', 'Unable to get data'));
      }
    };

    fetchData();
  }, [externalPurchaseId, api]);

  const renderStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Undefined', color: '#999' },
      1: { label: 'Booked', color: '#28a745' },
      2: { label: 'Cancelled', color: '#dc3545' },
      4: { label: 'Pending', color: '#ffc107' },
      5: { label: 'Pending Cancellation', color: '#fd7e14' }
    };

    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <span style={{
        backgroundColor: statusInfo.color,
        color: 'white',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {statusInfo.label}
      </span>
    );
  };

  const renderAttemptResult = (result) => {
    const resultMap = {
      0: { label: 'Undefined', color: '#999' },
      1: { label: 'Pending', color: '#ffc107' },
      2: { label: 'Failed', color: '#dc3545' },
      4: { label: 'Success', color: '#28a745' },
      5: { label: 'Cancellation Success', color: '#28a745' },
      6: { label: 'Cancellation Failed', color: '#dc3545' }
    };

    const resultInfo = resultMap[result] || resultMap[0];
    return (
      <span style={{
        backgroundColor: resultInfo.color,
        color: 'white',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '11px',
        fontWeight: 'bold'
      }}>
        {resultInfo.label}
      </span>
    );
  };

  const renderAttempts = (attempts) => {
    if (!attempts || attempts.length === 0) {
      return <div style={{ fontStyle: 'italic', color: '#666' }}>No attempts</div>;
    }

    return (
      <div style={{ marginTop: '10px' }}>
        {attempts.map((attempt, index) => (
          <div key={attempt.id} style={{
            background: '#f8f9fa',
            padding: '12px',
            marginBottom: '8px',
            borderRadius: '4px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Attempt #{attempts.length - index}</strong>
              {' '}
              <DisplayGuid baseAnchor='/externalpurchaseattempt'>{attempt.id}</DisplayGuid>
              {' '}
              {renderAttemptResult(attempt.result)}
            </div>
            <div style={{ fontSize: '13px', color: '#555' }}>
              <div><strong>Started:</strong> <DisplayDate>{attempt.startedAt}</DisplayDate></div>
              {attempt.endedAt && (
                <div><strong>Ended:</strong> <DisplayDate>{attempt.endedAt}</DisplayDate></div>
              )}
              {attempt.secretCode && (
                <div><strong>Secret Code:</strong> {attempt.secretCode}</div>
              )}
              {attempt.reference && (
                <div><strong>Reference:</strong> {attempt.reference}</div>
              )}
              {attempt.errorText && (
                <div style={{ marginTop: '6px' }}>
                  <strong>Error:</strong>
                  <div style={{
                    background: '#fff3cd',
                    padding: '8px',
                    marginTop: '4px',
                    borderRadius: '3px',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {attempt.errorText}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSignupStatus = (status) => {
    const statusMap = {
      0: 'Undefined',
      1: 'Booked',
      2: 'Cancelled'
    };
    return statusMap[status] || 'Unknown';
  };

  const renderProcessingStatus = (status) => {
    const statusMap = {
      0: 'Undefined',
      1: 'Pending',
      2: 'Success',
      3: 'Failure'
    };
    return statusMap[status] || 'Unknown';
  };

  const renderSignups = (signups) => {
    if (!signups || signups.length === 0) {
      return <div style={{ fontStyle: 'italic', color: '#666' }}>No signups</div>;
    }

    return (
      <div style={{ marginTop: '10px' }}>
        {signups.map((signup, index) => (
          <div key={signup.id} style={{
            background: '#f8f9fa',
            padding: '12px',
            marginBottom: '8px',
            borderRadius: '4px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Signup #{signups.length - index}</strong>
              {' '}
              <DisplayGuid baseAnchor='/signup'>{signup.id}</DisplayGuid>
            </div>
            <div style={{ fontSize: '13px', color: '#555' }}>
              <div><strong>Status:</strong> {renderSignupStatus(signup.status)}</div>
              <div><strong>Book Status:</strong> {renderProcessingStatus(signup.bookStatus)}</div>
              <div><strong>Cancellation Status:</strong> {renderProcessingStatus(signup.cancellationStatus)}</div>
              <div><strong>Reference:</strong> {signup.reference}</div>
              <div><strong>Participants:</strong> {signup.numberOfParticipants}</div>
              {signup.userEmail && (
                <div>
                  <strong>User:</strong> {signup.userEmail}
                  {signup.userId && (
                    <span> (<DisplayGuid baseAnchor='/user'>{signup.userId}</DisplayGuid>)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  let mappedData = null;

  if (externalPurchaseData) {
    const {
      id,
      createdAt,
      externalActivityId,
      externalActivityTitle,
      externalActivityStartsAt,
      externalActivityEndsAt,
      pricePaid,
      creditCardUsed,
      status,
      numberOfParticipants,
      isFromExistingSplitActivity,
      seasonalCourtSlotPurchaseId
    } = externalPurchaseData;

    mappedData = {
      'Id': <DisplayGuid>{id}</DisplayGuid>,
      'Created At': <DisplayDate>{createdAt}</DisplayDate>,
      'Status': renderStatusBadge(status),
      'Price Paid': pricePaid !== null && pricePaid !== undefined ? `${pricePaid} DKK` : '',
      'Credit Card Used': creditCardUsed || 'N/A',
      'Number of Participants': numberOfParticipants !== null && numberOfParticipants !== undefined ? numberOfParticipants : 'N/A',
      'Is From Existing Split Activity': isFromExistingSplitActivity.toString(),
      'External Activity': externalActivityId ? (
        <div>
          <DisplayGuid baseAnchor='/externalactivity'>{externalActivityId}</DisplayGuid>
        </div>
      ) : '',
      'Seasonal Court Slot Purchase': seasonalCourtSlotPurchaseId ?
        <DisplayGuid>{seasonalCourtSlotPurchaseId}</DisplayGuid> :
        'N/A'
    };
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={errorMessage} />

          <div className='content-with-right-pane-delimiter'>
            <PageTop
              documentTitle='External Purchase details'
              documentDescription='External Purchase details'
            />

            <h2 className='main-page-header main-page-header-with-subheader'>
              External Purchase
            </h2>
            <h5 className='main-page-subheader'>{externalPurchaseId}</h5>
            <br />

            {
              mappedData ? (
                <React.Fragment>
                  <DataDefinitionList
                    listData={mappedData}
                    classNames={['detail-page-list']}
                  />
                  <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>Signups</h3>
                  {renderSignups(externalPurchaseData.signups)}

                  <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>Purchase Attempts</h3>
                  {renderAttempts(externalPurchaseData.attempts)}
                </React.Fragment>
              ) : <Loader hasLoaded={false}>Loading...</Loader>
            }
          </div>

          <DetailsRightBar>
            <Divider>Links</Divider>
            <NoLink>None</NoLink>
            <Divider>Actions</Divider>
            <NoLink>None</NoLink>
          </DetailsRightBar>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const ExternalPurchase = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(ExternalPurchaseInternal)
  )
);

ExternalPurchase.requiresLogin = true;
ExternalPurchase.propTypes = {
  isLoggedIn: PropTypes.bool
};

export default ExternalPurchase;
